import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';

interface Props { marketId: string; marketQuestion?: string; resolvedOutcome?: string | null; currentUser?: string; }

export default function BettingPanel({ marketId, marketQuestion, resolvedOutcome, currentUser }: Props): React.JSX.Element {
  const isResolved = Boolean(resolvedOutcome);
  const [amount,  setAmount]  = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);

  async function placeBet(side: 'YES' | 'NO'): Promise<void> {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed < 1) {
      setIsError(true);
      setMessage('Minimum bet is $1.00.');
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch(`${API_BASE}/api/amm/bet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market_id: marketId,
          user_id:   currentUser || 'alice',
          side,
          amount:    parsed,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setIsError(true);
        setMessage(data.error ?? `HTTP ${res.status}`);
      } else {
        setIsError(false);
        setAmount('');
        const units  = data.shares_out != null ? Number(data.shares_out).toFixed(2) : '?';
        const price  = data.effective_price != null ? Number(data.effective_price).toFixed(4) : '?';
        const impact = data.price_impact_pct != null ? Number(data.price_impact_pct).toFixed(2) : '?';
        setMessage(`${side} · ${units} truth units @ $${price}/unit · ${impact}% slippage`);
      }
    } catch (e) {
      setIsError(true);
      setMessage((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        backgroundColor: '#0f172a',
        border:          '1px solid #1e293b',
        borderRadius:    8,
        padding:         '10px 16px',
        width:           '100%',
        fontFamily:      'monospace',
      }}
    >
      <p style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.22em', marginBottom: 8 }}>
        PLEDGE INSIGHT{marketQuestion
          ? <span style={{ color: '#94a3b8', letterSpacing: '0.04em', textTransform: 'none' }}>&nbsp;·&nbsp;{marketQuestion}</span>
          : null}
      </p>

      {/* ── Resolved banner ──────────────────────────────────────────────── */}
      {isResolved && (
        <div style={{
          background:    resolvedOutcome === 'YES' ? '#14532d' : '#450a0a',
          border:        `1px solid ${resolvedOutcome === 'YES' ? '#166534' : '#7f1d1d'}`,
          borderRadius:  4,
          padding:       '8px 12px',
          marginBottom:  10,
          textAlign:     'center',
          fontFamily:    'monospace',
          fontSize:      12,
          fontWeight:    700,
          letterSpacing: '0.1em',
          color:         resolvedOutcome === 'YES' ? '#86efac' : '#fca5a5',
        }}>
          MARKET RESOLVED: {resolvedOutcome} WON
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <label style={{ color: '#64748b', fontSize: 12 }}>$</label>
        <input
          type="number"
          min="0.01"
          step="0.01"
          placeholder="Min $1"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          disabled={loading}
          style={{
            flex:            1,
            backgroundColor: '#020617',
            border:          '1px solid #334155',
            borderRadius:    4,
            color:           '#e2e8f0',
            fontSize:        14,
            padding:         '6px 10px',
            outline:         'none',
          }}
        />
      </div>

      {/* ── YES / NO buttons — Tailwind for hover, inline for base ──────── */}
      <style>{`
        .bet-yes {
          flex: 1; padding: 8px 0; border-radius: 4px; font-size: 13px;
          letter-spacing: 0.1em; font-family: monospace; font-weight: 700;
          background: #052e16; color: #34d399; border: 1px solid #166534;
          cursor: pointer; transition: background 0.15s, box-shadow 0.15s, color 0.15s;
        }
        .bet-yes:hover:not(:disabled) {
          background: #14532d; color: #6ee7b7;
          box-shadow: 0 0 10px rgba(52,211,153,0.2);
        }
        .bet-yes:disabled { background: #0a1f14; color: #1f4d35; cursor: not-allowed; border-color: #0f2d1e; }

        .bet-no {
          flex: 1; padding: 8px 0; border-radius: 4px; font-size: 13px;
          letter-spacing: 0.1em; font-family: monospace; font-weight: 700;
          background: #2d0a0a; color: #f87171; border: 1px solid #7f1d1d;
          cursor: pointer; transition: background 0.15s, box-shadow 0.15s, color 0.15s;
        }
        .bet-no:hover:not(:disabled) {
          background: #450a0a; color: #fca5a5;
          box-shadow: 0 0 10px rgba(248,113,113,0.2);
        }
        .bet-no:disabled { background: #1a0a0a; color: #4a1a1a; cursor: not-allowed; border-color: #2d0d0d; }
      `}</style>

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="bet-yes" onClick={() => placeBet('YES')} disabled={loading || isResolved}>YES</button>
        <button className="bet-no"  onClick={() => placeBet('NO')}  disabled={loading || isResolved}>NO</button>
      </div>

      {message && (
        <p style={{
          marginTop:  12,
          fontSize:   11,
          color:      isError ? 'rgba(239,68,68,0.8)' : 'rgba(52,211,153,0.8)',
          lineHeight: 1.5,
        }}>
          {message}
        </p>
      )}
    </div>
  );
}
